import classNames from 'classnames';
import { AnchorLink } from 'components/anchorLink';
import { Icon, IconsEnum } from 'components/Icons';
import { ScommesseTableTabs } from 'components/scommesseTable/scommesseTableTabs';
import { Wrapper } from 'components/wrapper';
import { selectIsOpenCarrello } from 'features/carrello/carrelloSelectors';
import { selectTabListByTemplate } from 'features/sport';
import { SportManifestazioniTabs } from 'features/sport/components/SportManifestazioniTabs';
import { SportManifestazioniTabType } from 'features/sport/components/SportManifestazioniTabs/SportManifestazioniTabs';
import { SportTableScommesseHeadersSticky } from 'features/sport/components/sportTableScommesse/sportTableScommesseHeadersSticky';
import { useFilteredAvvenimentoCount, useInfoTipoScommessaInformation } from 'features/sport/hooks';
import { useGetLabelsByKeys } from 'hooks/useLingUI';
import useWindowSize from 'hooks/useWindowSize';
import { useTypedSelector } from 'lib/centralStore';
import { useRouter } from 'next/router';
import React, { SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { isTruthy, toSportUrlCase } from 'utility/functions';
import { LiveFiltersByGroup } from '../liveFiltersByGroup';
import { TabContent } from './components/TabContent';
import styles from './LiveInEvidenza.module.scss';

export type LiveInEvidenzaPropsType = {
  title?: string | null | undefined;
  hasFilter?: boolean;
  removeTabs?: boolean;
  hasPaddingLeft?: boolean;
  noPaddingRight?: boolean;
  hasPaddingRight?: boolean;
  isNotTopTemplate?: boolean;
  handleOpenFilters?: React.Dispatch<SetStateAction<boolean>>;
};

export const LiveInEvidenza = ({
  title,
  hasFilter,
  removeTabs,
  hasPaddingLeft,
  noPaddingRight,
  isNotTopTemplate,
  handleOpenFilters,
}: LiveInEvidenzaPropsType) => {
  const { asPath, isReady } = useRouter();
  const { isMobile } = useWindowSize();
  const [currentTab, setCurrentTab] = useState<string>('1');
  const [viewAllLabel, filtriLabel] = useGetLabelsByKeys(['view-all', 'filtri', 'prematch-filters-schedule-default']);
  const [selectedMobileTipoScommessa, setSelectedMobileTipoScommessa] = useState(0);

  useEffect(() => {
    setCurrentTab('1');
  }, [asPath]);

  const getCurrentTab = useCallback(
    (value: string) => {
      setCurrentTab(value);
      setSelectedMobileTipoScommessa(0);
    },
    [setCurrentTab]
  );

  const { keys } = useFilteredAvvenimentoCount();

  const isOpen = useTypedSelector(selectIsOpenCarrello);
  const tabList = useTypedSelector((st) => selectTabListByTemplate(st, 'templateLive'));

  const { tabIndex, selectedTab, memoizedTabList, infoTipoScommesseKeyListTab } = useMemo(() => {
    const memoizedTabList = tabList.map(
      ({ idTab, textTab, textTabTrKey }) => ({ idTab: `${idTab}`, textTab, textTabTrKey } as SportManifestazioniTabType)
    );

    const tabIndex = +currentTab - 1;
    const selectedTab = tabList?.[tabIndex];
    const infoTipoScommesseKeyListTab = (selectedTab?.infoTipoScommessaKeyList ?? []).filter(
      (x, idx, lst) => !!x && lst.indexOf(x) === idx
    );

    return { tabIndex, selectedTab, memoizedTabList, infoTipoScommesseKeyListTab };
  }, [tabList, currentTab]);

  const { infoTipoScommessaKeyList, infoTipoScommessaDescription } =
    useInfoTipoScommessaInformation(infoTipoScommesseKeyListTab);

  if (isTruthy(keys?.length)) {
    return (
      <Wrapper
        key={`Live-In-Evidenza-${asPath}-${isReady}`}
        noPaddingTop
        noPaddingLeft={isMobile || !hasPaddingLeft}
        noPaddingRight={noPaddingRight}
        noPaddingBottom
        bgColorTransparent
      >
        {isTruthy(tabList.length) && (
          <div className={`${styles.container} ${isOpen ? styles.cartOpen : ''}`}>
            <div className={styles.titleContainer}>
              {title && <h2 className={styles.title}>{title}</h2>}
              {hasFilter && (
                <>
                  <LiveFiltersByGroup />
                  <button
                    type="button"
                    className={styles.button}
                    onClick={() => handleOpenFilters!((prevState) => !prevState)}
                  >
                    <span className={styles.label}>{filtriLabel}</span>
                    <Icon iconId={IconsEnum.FILTER} color="var(--color-on-bg-primary)" className={styles.iconFilter} />
                  </button>
                </>
              )}
            </div>
            {/* sport Tabs  cambiare nome */}
            <SportManifestazioniTabs
              listTab={memoizedTabList}
              currentTab={currentTab}
              removeTabs={isTruthy(removeTabs)}
              handleClick={getCurrentTab}
            >
              <div className={styles.wrapperGroup} data-testid="LiveInEvidenza">
                {infoTipoScommessaDescription.length > 1 && (
                  <ScommesseTableTabs
                    activeIndex={selectedMobileTipoScommessa}
                    descriptions={infoTipoScommessaDescription}
                    onSelectedItem={setSelectedMobileTipoScommessa}
                  />
                )}
                <SportTableScommesseHeadersSticky
                  infoTipoScommessaKeyList={infoTipoScommessaKeyList}
                  className={classNames({ [styles.hidden]: isMobile })}
                />
                <TabContent
                  key={`tab-${tabIndex}`}
                  tab={selectedTab}
                  keys={keys}
                  isNotTopTemplate={isNotTopTemplate}
                  infoTipoScommessaIndex={selectedMobileTipoScommessa}
                  infoTipoScommesseKeyListTab={infoTipoScommesseKeyListTab}
                />
              </div>
              {isTruthy(tabList.length) && !removeTabs && (
                <div className={styles.footerTab} suppressHydrationWarning>
                  {selectedTab?.textTab && (
                    <AnchorLink
                      href={toSportUrlCase(`/live/${(tabList ?? {})[+currentTab - 1]?.textTab}`)}
                      className={styles.link}
                    >
                      {viewAllLabel}
                    </AnchorLink>
                  )}
                </div>
              )}
            </SportManifestazioniTabs>
          </div>
        )}
      </Wrapper>
    );
  }

  return null;
};
